<template>
  <div class="top-bar">
    <div class="file-identity">
      <button class="back-button" type="button" @click="$emit('back')" :aria-label="$t('audioPlayer.back')">
        <span class="material-icons" aria-hidden="true">arrow_back_ios_new</span>
      </button>
      <div class="title">
        <span class="title-eyebrow">{{ $t('audioPlayer.workspace') }}</span>
        <h2 :title="audioName">{{ audioName }}</h2>
      </div>
    </div>

    <div v-if="showDisplayModes" class="top-bar-actions">
      <div class="display-mode-toggle display-mode-toggle--aux">
        <el-tooltip
          v-if="displayMode === 'subtitles' && taskType !== 'tts'"
          :content="$t('audioPlayer.readAlongHint')"
          placement="top"
        >
          <button
            class="btn action-readalong"
            :class="{ 'btn-primary': isReadAlongMode, 'btn-secondary': !isReadAlongMode }"
            @click="$emit('toggle-read-along')"
          >
            <span class="player-icon icon-microphone"></span><span>{{ $t('audioPlayer.readAlongMode') }}</span>
          </button>
        </el-tooltip>

        <el-tooltip
          v-if="displayMode === 'subtitles' && taskType !== 'tts'"
          :content="$t('audioPlayer.smartSplitHint')"
          placement="top"
        >
          <div class="split-control action-split">
            <el-dropdown @command="$emit('split-type-change', $event)" trigger="click">
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="option in splitOptions"
                    :key="option.value"
                    :command="option.value"
                    :aria-checked="option.value === splitType"
                    role="menuitemradio"
                  >
                    <span class="split-option" :class="{ 'is-selected': option.value === splitType }">
                      <span class="split-option-text">
                        <span class="split-option-label">{{ option.label }}</span>
                        <span class="split-option-hint">{{ option.hint }}</span>
                      </span>
                      <span v-if="option.value === splitType" class="material-icons split-option-check" aria-hidden="true">check</span>
                    </span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
              <button class="btn" :class="{ 'btn-primary': useSmartSplit, 'btn-secondary': !useSmartSplit }">
                <span class="player-icon icon-split"></span>
                <span>{{ $t('audioPlayer.smartSplit') }}</span>
              </button>
            </el-dropdown>
          </div>
        </el-tooltip>

        <el-tooltip
          v-if="displayMode === 'subtitles' && taskType !== 'tts'"
          :content="$t('audioPlayer.translateHint')"
          placement="top"
        >
          <div class="translate-control action-translate">
            <el-dropdown @command="$emit('translate-language', $event)" trigger="click">
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="zh">中文</el-dropdown-item>
                  <el-dropdown-item command="en">English</el-dropdown-item>
                  <el-dropdown-item v-if="!demoTranslationOnly" command="ja">日本語</el-dropdown-item>
                  <el-dropdown-item v-if="!demoTranslationOnly" command="ko">한국어</el-dropdown-item>
                  <el-dropdown-item v-if="!demoTranslationOnly" command="es">Español</el-dropdown-item>
                  <el-dropdown-item v-if="!demoTranslationOnly" command="fr">Français</el-dropdown-item>
                  <el-dropdown-item v-if="!demoTranslationOnly" command="de">Deutsch</el-dropdown-item>
                  <el-dropdown-item command="none">{{ $t('audioPlayer.disableTranslation') }}</el-dropdown-item>
                </el-dropdown-menu>
              </template>
              <button class="btn" :class="{ 'btn-primary': isTranslated, 'btn-secondary': !isTranslated }">
                <span :class="['player-icon', isTranslating ? 'icon-loading' : 'icon-translate']"></span>
                {{ isTranslating ? $t('audioPlayer.translating') : $t('audioPlayer.translate') }}
              </button>
            </el-dropdown>
          </div>
        </el-tooltip>
      </div>

      <div v-if="taskType === 'tts'" class="generate-subtitles">
        <button class="btn btn-primary" @click="$emit('generate-subtitles')" :disabled="isGenerating">
          <span :class="['player-icon', isGenerating ? 'icon-loading' : 'icon-captions']"></span>
          {{ isGenerating ? $t('common.generating') : $t('common.generateSubtitles') }}
        </button>
      </div>

      <div class="display-mode-toggle display-mode-toggle--primary">
        <el-tooltip v-if="taskType !== 'tts'" :content="subtitlesButtonHint" placement="top">
          <button
            class="btn"
            :class="{ 'btn-primary': displayMode === 'subtitles', 'btn-secondary': displayMode === 'text' }"
            :aria-label="subtitlesButtonHint"
            @click="handleSubtitlesClick"
          >
            <span :class="['player-icon', subtitlesShown ? 'icon-captions' : 'icon-captions-off']"></span><span>{{ $t('audioPlayer.subtitlesMode') }}</span>
          </button>
        </el-tooltip>

        <el-tooltip :content="$t('audioPlayer.textHint')" placement="top">
          <button
            class="btn"
            :class="{ 'btn-primary': displayMode === 'text', 'btn-secondary': displayMode === 'subtitles' }"
            @click="$emit('set-display-mode', 'text')"
          >
            <span class="player-icon icon-text"></span><span>{{ $t('audioPlayer.textMode') }}</span>
          </button>
        </el-tooltip>
      </div>

    </div>

    <!-- 帮助：重看教程（字幕模式下才有）、意见反馈 -->
    <el-dropdown trigger="click" @command="$emit($event)">
      <button
        class="btn-icon mobile-more-button"
        type="button"
        :aria-label="$t('audioPlayer.tour.help')"
        :title="$t('audioPlayer.tour.help')"
      >
        <span class="material-icons-outlined tour-help-icon" aria-hidden="true">help_outline</span>
      </button>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item v-if="showTourButton" command="start-tour">{{ $t('audioPlayer.tour.replay') }}</el-dropdown-item>
          <el-dropdown-item command="open-feedback">{{ $t('userMenu.feedback') }}</el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>

    <button
      v-if="hasExportPanel && (!isMobile || showExportPanel)"
      class="btn-icon mobile-more-button"
      :class="{ active: showExportPanel }"
      :aria-label="$t('audioPlayer.moreSettings')"
      :aria-expanded="showExportPanel"
      @click="$emit('toggle-export-panel')"
    >
      <span class="more-dots"></span>
    </button>

    <el-dropdown v-else-if="isMobile" trigger="click" @command="handleMobileCommand">
      <button
        class="btn-icon mobile-more-button"
        :class="{ active: showExportPanel }"
        :aria-label="$t('audioPlayer.moreSettings')"
      >
        <span class="more-dots"></span>
      </button>
      <template #dropdown>
        <el-dropdown-menu class="mobile-player-menu">
          <template v-if="displayMode === 'subtitles' && taskType !== 'tts'">
            <el-dropdown-item
              v-for="option in splitOptions"
              :key="option.value"
              :command="`split:${option.value}`"
              :aria-checked="option.value === splitType"
              role="menuitemradio"
            >
              <span class="split-option" :class="{ 'is-selected': option.value === splitType }">
                <span class="split-option-text">
                  <span class="split-option-label">{{ option.label }}</span>
                  <span class="split-option-hint">{{ option.hint }}</span>
                </span>
                <span v-if="option.value === splitType" class="material-icons split-option-check" aria-hidden="true">check</span>
              </span>
            </el-dropdown-item>
          </template>
          <el-dropdown-item v-if="displayMode === 'subtitles' && taskType !== 'tts'" divided command="translate:zh">
            中文
          </el-dropdown-item>
          <el-dropdown-item v-if="displayMode === 'subtitles' && taskType !== 'tts'" command="translate:en">
            English
          </el-dropdown-item>
          <el-dropdown-item v-if="displayMode === 'subtitles' && taskType !== 'tts' && !demoTranslationOnly" command="translate:ja">
            日本語
          </el-dropdown-item>
          <el-dropdown-item v-if="displayMode === 'subtitles' && taskType !== 'tts' && !demoTranslationOnly" command="translate:ko">
            한국어
          </el-dropdown-item>
          <el-dropdown-item v-if="displayMode === 'subtitles' && taskType !== 'tts' && !demoTranslationOnly" command="translate:es">
            Español
          </el-dropdown-item>
          <el-dropdown-item v-if="displayMode === 'subtitles' && taskType !== 'tts' && !demoTranslationOnly" command="translate:fr">
            Français
          </el-dropdown-item>
          <el-dropdown-item v-if="displayMode === 'subtitles' && taskType !== 'tts' && !demoTranslationOnly" command="translate:de">
            Deutsch
          </el-dropdown-item>
          <el-dropdown-item v-if="displayMode === 'subtitles' && taskType !== 'tts'" command="translate:none">
            {{ $t('audioPlayer.disableTranslation') }}
          </el-dropdown-item>
          <el-dropdown-item v-if="hasExportPanel" divided command="export">
            {{ $t('audioPlayer.moreSettings') }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<script>
export default {
  name: 'PlayerTopBar',
  emits: [
    'back',
    'generate-subtitles',
    'set-display-mode',
    'toggle-read-along',
    'split-type-change',
    'toggle-subtitles',
    'translate-language',
    'toggle-export-panel',
    'start-tour',
    'open-feedback',
  ],
  props: {
    audioName: { type: String, required: true },
    taskType: { type: String, required: true },
    isGenerating: { type: Boolean, default: false },
    displayMode: { type: String, required: true },
    isReadAlongMode: { type: Boolean, default: false },
    useSmartSplit: { type: Boolean, default: false },
    isSubtitlesVisible: { type: Boolean, default: true },
    isTranslated: { type: Boolean, default: false },
    isTranslating: { type: Boolean, default: false },
    isMobile: { type: Boolean, default: false },
    showExportPanel: { type: Boolean, default: false },
    // 有导出、下载等工具时才需要"更多"面板；视频没有这些工具
    hasExportPanel: { type: Boolean, default: true },
    demoTranslationOnly: { type: Boolean, default: false },
    demoVideoMode: { type: Boolean, default: false },
    showTourButton: { type: Boolean, default: false },
    hasShortPractice: { type: Boolean, default: false },
    hasPause: { type: Boolean, default: false },
    splitType: { type: String, default: 'practice' },
  },
  computed: {
    splitOptions() {
      const options = [
        { value: 'practice', label: 'splitByPractice' },
        { value: 'practice_short', label: 'splitByPracticeShort', hidden: !this.hasShortPractice },
        { value: 'comma', label: 'splitByComma', hidden: this.demoVideoMode },
        { value: 'period', label: 'splitByPeriod' },
        { value: 'pause', label: 'splitByPause', hidden: !this.hasPause },
        { value: 'none', label: 'noSplit', hidden: this.demoVideoMode || this.hasPause },
      ]
      return options
        .filter(option => !option.hidden)
        .map(option => ({
          value: option.value,
          label: this.$t(`audioPlayer.${option.label}`),
          hint: this.$t(`audioPlayer.splitHints.${option.value}`),
        }))
    },
    showDisplayModes() {
      return Boolean(this.taskType)
    },
    subtitlesShown() {
      return this.displayMode !== 'subtitles' || this.isSubtitlesVisible
    },
    // 字幕模式下再点"字幕"切换显示和隐藏
    subtitlesButtonHint() {
      if (this.displayMode !== 'subtitles') return this.$t('audioPlayer.subtitlesHint')
      return this.isSubtitlesVisible ? this.$t('audioPlayer.hideSubtitles') : this.$t('audioPlayer.showSubtitles')
    },
  },
  methods: {
    handleSubtitlesClick() {
      if (this.displayMode === 'subtitles') {
        this.$emit('toggle-subtitles')
        return
      }
      this.$emit('set-display-mode', 'subtitles')
    },
    handleMobileCommand(command) {
      if (command.startsWith('split:')) {
        this.$emit('split-type-change', command.replace('split:', ''))
        return
      }
      if (command.startsWith('translate:')) {
        this.$emit('translate-language', command.replace('translate:', ''))
        return
      }
      if (command === 'export') {
        this.$emit('toggle-export-panel')
      }
    },
  },
}
</script>

<style scoped>
.file-identity {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.title-eyebrow {
  display: block;
  margin-bottom: 0.15rem;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.player-icon {
  display: block;
  width: 17px;
  height: 17px;
  flex: 0 0 17px;
  background: currentColor;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;
}

/* 与素材库分类页的返回按钮保持一致 */
.file-identity .back-button {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(16, 24, 32, 0.07);
  color: var(--ink-primary);
  box-shadow: none;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.file-identity .back-button .material-icons {
  margin-right: 2px;
  font-size: 18px;
}

.file-identity .back-button:hover {
  background: rgba(16, 24, 32, 0.12);
}

.file-identity .back-button:active {
  transform: scale(0.94);
}

.file-identity .back-button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}
.icon-captions { mask-image: url('~@/assets/ui-icons/captions.svg'); -webkit-mask-image: url('~@/assets/ui-icons/captions.svg'); }
.icon-captions-off { mask-image: url('~@/assets/ui-icons/captions-off.svg'); -webkit-mask-image: url('~@/assets/ui-icons/captions-off.svg'); }
.icon-microphone { mask-image: url('~@/assets/ui-icons/microphone.svg'); -webkit-mask-image: url('~@/assets/ui-icons/microphone.svg'); }
.icon-split { mask-image: url('~@/assets/ui-icons/split.svg'); -webkit-mask-image: url('~@/assets/ui-icons/split.svg'); }
.icon-eye { mask-image: url('~@/assets/ui-icons/eye.svg'); -webkit-mask-image: url('~@/assets/ui-icons/eye.svg'); }
.icon-translate { mask-image: url('~@/assets/ui-icons/translate.svg'); -webkit-mask-image: url('~@/assets/ui-icons/translate.svg'); }
.icon-text { mask-image: url('~@/assets/ui-icons/text.svg'); -webkit-mask-image: url('~@/assets/ui-icons/text.svg'); }
.icon-loading {
  width: 15px;
  height: 15px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  background: transparent;
  mask-image: none;
  -webkit-mask-image: none;
  animation: topBarSpin 0.8s linear infinite;
}

.tour-help-icon {
  font-size: 20px;
}

.more-dots,
.more-dots::before,
.more-dots::after {
  width: 3px;
  height: 3px;
  background: currentColor;
  border-radius: 50%;
}

.more-dots {
  position: relative;
}

.more-dots::before,
.more-dots::after {
  content: '';
  position: absolute;
  left: 0;
}

.more-dots::before { top: -6px; }
.more-dots::after { top: 6px; }

@keyframes topBarSpin {
  to { transform: rotate(360deg); }
}

.translate-control {
  display: inline-flex;
  align-items: center;
}

/* 右上角的设置开关：和侧边栏菜单项同一种选中样式 */
.top-bar .mobile-more-button {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--ink-secondary);
  box-shadow: none;
  cursor: pointer;
  transition: background-color 160ms ease, color 160ms ease;
}

.top-bar .mobile-more-button:hover {
  background: var(--surface-subtle);
  color: var(--ink-primary);
}

.top-bar .mobile-more-button.active {
  background: var(--surface-active);
  color: var(--signal-active-strong);
}

.top-bar .mobile-more-button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.top-bar-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  margin-left: auto;
}

.display-mode-toggle--aux {
  margin-right: 0;
}

.display-mode-toggle--primary {
  margin-right: 0;
  flex-shrink: 0;
}

:deep(.el-dropdown-menu__item) {
  padding: 8px 16px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.split-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 232px;
}

.split-option-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.split-option-label {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  line-height: 1.4;
}

.split-option.is-selected .split-option-label {
  font-weight: 600;
}

.split-option-hint {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.5;
  white-space: normal;
}

.split-option-check {
  flex: 0 0 auto;
  color: var(--signal-active-strong);
  font-size: 18px;
}

:deep(.mobile-player-menu .el-dropdown-menu__item) {
  min-width: 132px;
  font-size: 0.86rem;
}

@media (max-width: 768px) {
  .top-bar {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-auto-flow: column;
    align-items: center;
    gap: 0.35rem;
    margin-bottom: 0;
    overflow: hidden;
  }

  .file-identity {
    gap: 0.45rem;
    min-width: 0;
  }

  .title {
    display: none;
  }

  .top-bar-actions {
    min-width: 0;
    justify-content: flex-end;
    gap: 0.25rem;
    margin-left: auto;
    overflow: hidden;
  }

  .display-mode-toggle--aux {
    display: flex;
    order: 1;
    padding: 0;
    background: transparent;
  }

  .display-mode-toggle--primary {
    order: 2;
  }

  .display-mode-toggle--aux .action-split,
  .display-mode-toggle--aux .action-translate {
    display: none;
  }

  .display-mode-toggle {
    flex: 0 0 auto;
    gap: 0.16rem;
    margin: 0;
    padding: 0.18rem;
    border-radius: 10px;
    align-items: center;
  }

  .top-bar .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    min-width: 32px;
    min-height: 32px;
    height: 32px;
    padding: 0;
    gap: 0;
    border-radius: 8px;
    font-size: 0;
    line-height: 1;
    white-space: nowrap;
  }

  .action-readalong {
    min-width: 32px;
  }

  .player-icon {
    width: 14px;
    height: 14px;
    flex-basis: 14px;
  }

  .btn-icon {
    width: 32px;
    min-width: 32px;
    height: 32px;
    margin-left: 0;
  }
}
</style>
